/*
 * Copyright '2025' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { css, html, LitElement } from "lit";
import { property } from "lit/decorators.js";
import { FeedbackHandler } from "../handler/feedback-handler";
import { SessionStorageService } from "../../services/session-storage/session-storage.service";
import { TranslationService } from "sirius-platform-support-library/shared/localization/translations/translation.service";
import { SupportedTranslationsLoaders } from "sirius-platform-support-library/shared/localization/translations/loaders/translations-loaders.constants";
import { environment } from "../../../../environments/environments";

export const FeedbackWebElementTypeName = "FeedbackWebElement";

export class FeedbackWebElement extends LitElement {
  public static readonly CUSTOM_ELEMENT_NAME = "tdp-feedback";
  public static readonly CHECKBOX_DESCRIPTION =
    "DIALOGS.FEEDBACK.CHECKBOX_DESCRIPTION";
  public static readonly FEEDBACK_DESCRIPTION = "DIALOGS.FEEDBACK.DESCRIPTION";

  private sessionStorage: SessionStorageService;
  private translationService?: TranslationService;

  @property()
  public checkboxDescription: string;

  @property()
  public feedbackDescription: string;

  @property()
  public checkboxValue: string;

  static styles = css`
    .helper-text {
      color: #0e0e0e;
      font-size: 14px;
      font-family: Roboto Light;
      font-weight: 400;
      line-height: 24px;
      text-decoration: none;
      letter-spacing: 0;
    }

    .clr-checkbox-wrapper {
      position: relative;
    }

    .input-checkbox {
      position: absolute;
      opacity: 1;
      left: 0;
      height: 1rem;
      width: 1rem;
    }

    .label {
      padding: 4px 8px 4px 41px;
      margin: 0 16px 8px -8px;
      border-radius: 4px;
      color: #0e0e0e;
      font-size: 16px;
      font-family: Roboto;
      font-weight: 400;
      line-height: 24px;
      text-decoration: none;
      letter-spacing: 0;
    }

    .checkbox-container {
      margin-top: 1.2rem;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .label:hover {
      background-color: #e1e1e1;
      border-color: #e1e1e1;
    }
  `;

  public constructor() {
    super();
    this.translationService = new TranslationService({
      publicPath: {
        provide: () => {
          // @ts-ignore
          return __webpack_public_path__;
        },
      },
      type: SupportedTranslationsLoaders.REMOTE_ASSETS,
      options: {
        baseUrl:
          environment.cdnBaseURL +
          "tdp-assets-library" +
          environment.assetsLibraryVersion,
        resourceNameMapper: (localeCode) =>
          "/resources/tdp-common/" + localeCode.replace(/_/g, "-") + ".json",
      },
    });
    this.translationService.onLanguageChanged(this, this.translateProperties);

    this.sessionStorage = new SessionStorageService(window);
  }

  private async translateProperties(): Promise<void> {
    this.checkboxDescription = await this.translationService.translate(
      FeedbackWebElement.CHECKBOX_DESCRIPTION
    );
    this.feedbackDescription = await this.translationService.translate(
      FeedbackWebElement.FEEDBACK_DESCRIPTION
    );
    this.requestUpdate();
  }

  public render() {
    return html`
            <span class="helper-text">${this.feedbackDescription}</p>
                <clr-chekbox-container class="checkbox-container">
            <clr-checkbox-wrapper class="clr-checkbox-wrapper">
                <input class="input-checkbox" type="checkbox" clrCheckbox .checked=${
                  this.checkboxValue === "true"
                }
                       name="checkBox"
                       @click=${this.handleCheckboxClick}/>
                <label class="label">${this.checkboxDescription}</label>
            </clr-checkbox-wrapper>
            </clr-chekbox-container>
        `;
  }

  private handleCheckboxClick(): void {
    this.checkboxValue = `${this.checkboxValue !== "true"}`;
    this.sessionStorage.setItem(
      FeedbackHandler.FEEDBACK_ACTION_EVENT,
      this.checkboxValue
    );
  }

  public async connectedCallback() {
    super.connectedCallback();
    await this.translateProperties();
  }

  public disconnectedCallback() {
    super.disconnectedCallback();
  }

  public static register() {
    if (!customElements.get(FeedbackWebElement.CUSTOM_ELEMENT_NAME)) {
      customElements.define(
        FeedbackWebElement.CUSTOM_ELEMENT_NAME,
        FeedbackWebElement
      );
    }
  }
}
